import { useMemo, useState } from 'react'
import {
  AssignmentLane, Button, Card, CardBody, CardHeader, Dialog, DialogContent,
  DialogDescription, DialogTitle, EmptyState, ErrorState, Field,
  LaneGrid, LoadingState, NoPermissionState, TaskCard,
  formatClockMinute, formatCount, formatCredits, useLocale, cn,
} from '@hk/ui'
import { CalendarPlus, Clock, TriangleAlert, Wand2 } from 'lucide-react'
import {
  ApiError,
  type BoardLane,
  type BoardTask,
  nameIn,
  translateError,
  type UnplacedReason,
  type UnplacedRoom,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { balanceAdvice, canReceiveWork, workloadOf } from '@hk/core'
import { type AssignmentBoardState, useAssignmentBoard } from './useAssignmentBoard'

type Strings = (typeof STRINGS)['en']

/* ============================================================================
   W-40 — the assignment board. The second XL screen.

   TWO WAYS TO MOVE A CARD, and that is deliberate.

   Drag and drop is what a supervisor expects and what the design shows. It is also
   unusable with a keyboard and invisible to a screen reader, so it is an ENHANCEMENT
   layered on top of a path that works without it: select a card, then press "Assign
   here" on a lane. Both call the same mutation. A board where the only way to move
   work is a mouse gesture excludes people from the job, not just from the feature.

   Lanes for staff who are not on shift are inert as DROP TARGETS but their cards stay
   draggable OUT. A lane that is inert in both directions strands the work of whoever
   called in sick — which is precisely the work a supervisor opened this screen to move.
   ========================================================================= */

export function AssignmentBoard({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const board = useAssignmentBoard(propertyId)
  const [selected, setSelected] = useState<string | null>(null)

  /* SPRINT 34. Two pieces of state for one control: whether the form is open, and what
     the last successful save set — held so the confirmation survives the form closing.

     IT IS HELD HERE AND NOT IN THE HOOK, unlike the sweep summary beside it, and the
     difference is what each one is for. The summary names rooms nobody can clean: that is
     a job somebody has to go and do, so it waits until they dismiss it. This is a receipt
     for a setting they just changed, on a line that now says the same thing in the
     toolbar — it is cleared by the next interaction, and does not need dismissing. */
  const [schedOpen, setSchedOpen] = useState(false)
  const [schedSaved, setSchedSaved] = useState<{ minute: number | null } | null>(null)

  const openSchedule = () => {
    setSchedSaved(null)
    board.clearScheduleError()
    setSchedOpen(true)
  }

  const advice = useMemo(() => balanceAdvice(board.data?.lanes ?? []), [board.data])

  const move = (taskId: string, toUserId: string) => {
    board.assign(taskId, toUserId)
    setSelected(null)
  }

  /* `isLoading` alone is NOT enough, and the difference is only visible against a
     server that keeps failing.

     react-query reports `isLoading` whenever a query with no data is fetching — which
     includes every RETRY. So a board that has never loaded goes: skeleton, error,
     skeleton, error, once per refresh interval. A supervisor glancing at it catches the
     skeleton and reads "slow"; the truth is "the database is down" and it is on screen
     for only part of each cycle.

     Once something has failed, the error holds the screen. The retry still happens in
     the background, and a success still replaces it. */
  if (board.isLoading && !board.error) return <LoadingState rows={6} />

  if (board.error && !board.data) {
    if (board.error instanceof ApiError && board.error.code === 'permission.denied')
      return <NoPermissionState />

    const translated = translateError(board.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={board.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const view = board.data!
  const hasWork = view.unassigned.length > 0 || view.lanes.some((l) => l.tasks.length > 0)

  /* **THE ROOMS THE PLAN COULD NOT PLACE, GATHERED UNDER THE REASON THEY SHARE.**
     Sprint 81. One sentence over a mixed list was survivable while the reasons rarely
     occurred together and became a lie when CQ-145 added a fourth: rooms nobody has said
     how to clean and rooms behind a full floor are two different jobs for two different
     people. The order is the order the reasons first appear, which is the plan's own
     heaviest-first order and therefore stable between two presses.

     **PLAINLY COMPUTED AND NOT `useMemo`'d**, because this line sits below the screen's
     early returns and a hook below an early return is a hook that is sometimes not called
     — which is not a slow screen, it is a blank one. Grouping at most a few dozen rooms
     on a render nobody can perceive is not worth a rule of hooks. */
  const unplacedByReason: [UnplacedReason, UnplacedRoom[]][] = (() => {
    const groups = new Map<UnplacedReason, UnplacedRoom[]>()
    for (const room of board.proposal?.unplaced ?? [])
      (groups.get(room.reason) ?? groups.set(room.reason, []).get(room.reason)!).push(room)
    return [...groups.entries()]
  })()

  const card = (task: BoardTask, laneUserId?: string) => {
    /* #116. FROM THE SERVER, from the same function the assignment gate uses — not
       `task.credits === 0`, which is the symptom rather than the rule and would be a
       second opinion about it. */
    const notConfigured = task.missingCleaningRules.length > 0

    return (
    <div
      key={task.taskId}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', task.taskId)
        e.dataTransfer.effectAllowed = 'move'
        setSelected(task.taskId)
      }}
      onDragEnd={() => setSelected(null)}
    >
      <button
        type="button"
        aria-pressed={selected === task.taskId}
        onClick={() => setSelected(selected === task.taskId ? null : task.taskId)}
        className={cn(
          'block w-full text-start',
          selected === task.taskId && 'rounded-[7px] ring-3 ring-[rgb(56_189_248/0.35)]',
          board.movingTaskId === task.taskId && 'opacity-60',
        )}
        // The full sentence, because a sighted supervisor gets room, type and cost from
        // three positions on the card at once — and, since #116, the one thing a card
        // can say that changes what they do with it.
        aria-label={
          `${task.roomNumber}. ${localisedType(task, locale)}. ` +
          `${formatCredits(task.credits, locale)} ${t.credits}.` +
          (laneUserId ? '' : ` ${t.unassigned}.`) +
          (notConfigured ? ` ${fill(t.cardNotConfiguredWhy, { room: task.roomNumber })}` : '')
        }
      >
        <TaskCard
          room={task.roomNumber}
          detail={
            `${localisedType(task, locale)} · ${nameIn(locale, task.roomType, task.roomTypeAr)}`
          }
          time={
            /* **#116 — A CARD THAT CANNOT BE HANDED OUT SAYS SO WHERE THE COST WOULD BE.**
            
               The cost is the one thing on this card that is meaningless for such a room:
               "0.00 cr" is arithmetic on minutes nobody has set, and it reads as a light
               room. Replacing it is better than adding a badge beside it — the supervisor
               is already looking at that corner to decide where the card goes.

               AMBER, NOT RED, and the same colour W-127 uses for the same rooms. Nothing
               is broken and nobody has done anything wrong: a room arrived and nobody has
               said yet how it is cleaned.

               **THE CARD STAYS DRAGGABLE.** Making it inert would leave a supervisor with
               a card they cannot move and no way to find out why — and the refusal, when
               they try, names the room and the screen that fixes it. *A disabled control
               must leave somebody something to press.* */
            notConfigured
              ? (
                <span className="rounded-hk bg-[#fffbeb] px-1.5 py-0.5 text-[10.5px]
                                 font-medium text-[#92400e] dark:bg-[#78350f]/40
                                 dark:text-[#fcd34d]">
                  {t.cardNotConfigured}
                </span>
              )
              : (
                <>
                  {formatCredits(task.credits, locale)} {t.creditsShort}
                  {board.movingTaskId === task.taskId && <> · {t.moving}</>}
                </>
              )
          }
        />
      </button>
    </div>
    )
  }

  return (
    <div className="flex flex-col gap-3.5">
      {board.assignError ? (
        <div role="alert" className="rounded-lg border border-[#fca5a5] bg-[#fef2f2] px-3 py-2 text-[12px] text-[#b91c1c] dark:border-[#7f1d1d] dark:bg-[#2a0f0f] dark:text-[#fca5a5]">
          {String(translateError(board.assignError, locale).message)}
        </div>
      ) : null}

      {/* ── THE SCHEDULE DID NOT RUN (Sprint 34) ──────────────────────────────
          FIRST ON THE SCREEN, ABOVE EVERYTHING, because it is the answer to the question
          somebody is already asking when they open this board and it is empty.

          Sprint 33 argued against a background job on exactly this ground: a hotel whose
          board is bare at half past six needs to know whether the job failed or whether
          there is genuinely nothing to clean, and automation that cannot tell them apart
          makes a quiet morning indistinguishable from a broken server. Adding the
          scheduler without this banner would have proved that objection right.

          RED, NOT AMBER — the one place on this screen that is. Everything else drawn in
          amber here is a job somebody has not done yet; this is a thing that was supposed
          to happen by itself and did not, and the hotel is running late because of it.

          The server decides this, in `WorkSchedule.IsOverdue`, and the flag is used as
          given. The minute is read only to write the time into the sentence — this is NOT
          a second opinion about whether the run is late (#113: two copies of a rule agree
          with each other right up until one of them is wrong). */}
      {view.scheduledRunIsOverdue && view.generateWorkAtLocalMinute !== null && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-[#fca5a5] bg-[#fef2f2]
                     px-3 py-2 text-[12px] text-[#b91c1c]
                     dark:border-[#7f1d1d] dark:bg-[#2a0f0f] dark:text-[#fca5a5]"
        >
          <TriangleAlert className="mt-px size-4 shrink-0" />
          <span>
            {fill(t.schedOverdue, {
              time: formatClockMinute(view.generateWorkAtLocalMinute, locale),
            })}
          </span>
        </div>
      )}

      {/* THE BUTTON IS ALWAYS AVAILABLE, not only on an empty board. Departures arrive
          through the morning as the property system updates occupancy, and a supervisor
          who swept at six presses again at ten to pick them up — which is safe, and
          reports zero when there is nothing new.

          THE SCHEDULE LINE IS DRAWN WHETHER OR NOT THERE IS WORK, and the button is not.
          An empty board already carries a button inside its empty state; what it does not
          carry, and most needs, is the sentence saying whether anything was going to fill
          it without being asked. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {hasWork && <GenerateButton board={board} t={t} />}
        {/* **W-42 — THE SYSTEM PROPOSES.** Beside the sweep, because they are the two
            halves of filling a board: one raises the work, the other says who should do
            it. Only when there is something to allocate — a button that produces an empty
            plan teaches a supervisor that the button does nothing. */}
        {view.unassigned.length > 0 && !board.proposal && (
          <Button type="button" variant="outline" onClick={board.propose}>
            <Wand2 className="size-3.5" />
            {t.propButton}
          </Button>
        )}
        <ScheduleLine
          minute={view.generateWorkAtLocalMinute}
          locale={locale}
          t={t}
          onChange={openSchedule}
        />
      </div>

      {schedSaved && (
        <div
          role="status"
          className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-[12px]"
        >
          {schedSaved.minute === null
            ? t.schedSavedOff
            : fill(t.schedSavedOn, { time: formatClockMinute(schedSaved.minute, locale) })}
        </div>
      )}

      {schedOpen && (
        <ScheduleDialog
          board={board}
          t={t}
          locale={locale}
          current={view.generateWorkAtLocalMinute}
          onClose={() => setSchedOpen(false)}
          onSaved={(minute) => { setSchedSaved({ minute }); setSchedOpen(false) }}
        />
      )}

      {advice && (
        <div role="status" className="rounded-lg border border-[#fcd34d] bg-[#fef3c7] px-3 py-2 text-[12px] text-[#b45309] dark:border-[#b45309] dark:bg-[#2a1e08] dark:text-[#fbbf24]">
          {/* Names the person who is over AND the person with room. "Unbalanced" makes
              a supervisor scan four lanes to find the fix. */}
          {fill(t.overBudget, {
            name: laneName(advice.over.lane, locale),
            by: formatCredits(advice.over.by, locale),
          })}
          {advice.freest && ' ' + fill(t.hasCapacity, {
            name: laneName(advice.freest.lane, locale),
            free: formatCredits(advice.freest.free, locale),
          })}
        </div>
      )}

      {/* ── WHERE THE WORK COMES FROM (Sprint 33) ─────────────────────────────
          A summary that says what the sweep DID, held until it is dismissed rather than
          cleared by the next refetch — the rooms nobody can clean are the point of it,
          and the very next thing a supervisor does is drag a card. */}
      {board.generateError ? (
        <div role="alert" className="rounded-lg border border-[#fca5a5] bg-[#fef2f2] px-3 py-2 text-[12px] text-[#b91c1c] dark:border-[#7f1d1d] dark:bg-[#2a0f0f] dark:text-[#fca5a5]">
          {String(translateError(board.generateError, locale).message)}
        </div>
      ) : null}

      {board.generated && (
        <div
          role="status"
          className="flex flex-col gap-1.5 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-[12px]"
        >
          <div className="flex items-start gap-2">
            <span className="font-medium">
              {board.generated.raised > 0
                ? fill(t.genRaised, { count: formatCount(board.generated.raised, locale) })
                : t.genNothingNew}
            </span>
            <button
              type="button"
              onClick={board.dismissGenerated}
              className="ms-auto shrink-0 text-[11px] text-muted-fg underline cursor-pointer"
            >
              {t.genDismiss}
            </button>
          </div>

          <span className="tnum text-[11.5px] text-muted-fg">
            {fill(t.genBreakdown, {
              departures: formatCount(board.generated.departures, locale),
              stayovers: formatCount(board.generated.stayovers, locale),
              inHand: formatCount(board.generated.alreadyInHand, locale),
              nothing: formatCount(board.generated.nothingToClean, locale),
              deep: formatCount(board.generated.deepCleans, locale),
              refresh: formatCount(board.generated.refreshes, locale),
              shut: formatCount(board.generated.notInService, locale),
              swept: formatCount(board.generated.roomsSwept, locale),
            })}
          </span>

          {/* **CQ-26 — WHAT THIS HOTEL DOES NOT RAISE, AND HOW MANY ROOMS WANTED IT.**
              Sprint 83. A board that is thinner than expected has exactly two innocent
              explanations and one alarming one; this is the sentence that tells them
              apart. Drawn plainly rather than in amber: a switch the hotel chose is not
              a warning. */}
          {board.generated.switchedOff.length > 0 && (
            <span className="text-[11.5px] text-muted-fg">
              {board.generated.switchedOff.map((x) => fill(t.genSwitchedOff, {
                count: formatCount(x.rooms, locale),
                what: t.trgNames[x.code as keyof typeof t.trgNames] ?? x.code,
              })).join(' · ')}
            </span>
          )}

          {/* AMBER, NOT RED — the same colour and the same reasoning as W-127's rows.
              Nothing is broken and nobody has done anything wrong: a room arrived and
              nobody has said yet how it is cleaned. */}
          {board.generated.unassignableRooms.length > 0 && (
            <span className="rounded-hk border border-[#fcd34d] bg-[#fffbeb] px-2.5 py-1.5
                             text-[11.5px] text-[#92400e] dark:border-[#b45309]
                             dark:bg-[#78350f]/25 dark:text-[#fcd34d]">
              {fill(t.genUnassignable, {
                rooms: board.generated.unassignableRooms.join('، '),
              })}
            </span>
          )}
        </div>
      )}

      {/* ── W-42 · THE PLAN, AND IT IS ONLY A PLAN (Sprint 73) ──────────────────────
          The client's answer: *"System proposes, supervisor approves."* Nothing on the
          board has moved. It is drawn as a list rather than as ghosts in the lanes
          deliberately — a proposal shown in place looks like a board that has already been
          changed, and the supervisor's own eye is the thing being asked to check it. */}
      {board.proposal && (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-[12px]"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">
              {board.proposal.placements.length === 0
                ? t.propNothing
                : fill(t.propHeadline, {
                  count: formatCount(board.proposal.placements.length, locale),
                })}
            </span>
            <span className="text-muted-fg">{t.propNothingMoved}</span>
          </div>

          {/* **WHAT IT COULD NOT PLACE, BEFORE THE APPROVE BUTTON AND NOT AFTER IT.** A
              plan that quietly covers eleven of fifteen rooms is one somebody approves and
              then discovers.

              **ONE LINE PER REASON, SINCE SPRINT 81.** It drew the rooms in a single list
              and then printed the FIRST room's sentence under all of them, which was
              survivable while the three reasons were rare together and became a lie the
              moment CQ-145 added a fourth: three rooms nobody can clean and two behind a
              full floor are two different problems for two different people, and one
              sentence over both sends the supervisor to fix the wrong one. */}
          {unplacedByReason.length > 0 && unplacedByReason.map(([reason, rooms]) => (
            <span
              key={reason}
              className="rounded-hk border border-[#fcd34d] bg-[#fffbeb] px-2.5 py-1.5
                         text-[11.5px] text-[#92400e] dark:border-[#b45309]
                         dark:bg-[#78350f]/25 dark:text-[#fcd34d]"
            >
              {fill(t.propUnplaced, {
                count: formatCount(rooms.length, locale),
                rooms: rooms.map((u) => u.task.roomNumber).join('، '),
              })}
              {' '}
              {t.propUnplacedWhy[reason]}
            </span>
          ))}

          {/* **AND WHO WOULD BE STANDING IDLE.** CQ-141 puts the floor first, which is
              plainly right for one room and produces allocations across a whole morning
              that nobody would defend — the first render of this screen gave three
              third-floor rooms to the man already at 10.75 of 18 and left two attendants
              with nothing. It is SHOWN rather than corrected: bounding the floor
              preference means inventing a threshold for what a walk costs, and this
              product has no such measure. That question is CQ-145. Until it is answered,
              the supervisor sees the consequence and overrules with a drag — which is what
              "the supervisor approves" is for. */}
          {board.proposal.wouldBeIdle.length > 0 && (
            <span className="rounded-hk border border-[#fcd34d] bg-[#fffbeb] px-2.5 py-1.5
                             text-[11.5px] text-[#92400e] dark:border-[#b45309]
                             dark:bg-[#78350f]/25 dark:text-[#fcd34d]">
              {fill(t.propIdle, {
                names: board.proposal.wouldBeIdle
                  .map((l) => (locale === 'ar' && l.labelAr ? l.labelAr : l.label))
                  .join('، '),
              })}
            </span>
          )}

          <ul className="flex flex-col gap-0.5">
            {board.proposal.placements.map((p) => (
              <li key={p.task.taskId} className="tnum text-[11.5px]">
                {fill(t.propRow, {
                  room: p.task.roomNumber,
                  name: p.toLabelAr && locale === 'ar' ? p.toLabelAr : p.toLabel,
                })}
                {/* **WHICH ONES ARE THE OBVIOUS PLACEMENTS AND WHICH ARE THE
                    COMPROMISES.** CQ-141 puts the floor first; a supervisor reading a plan
                    is entitled to see where that held and where it did not. */}
                {!p.onThisFloor && (
                  <span className="ms-1.5 text-muted-fg">{t.propOffFloor}</span>
                )}
              </li>
            ))}
          </ul>

          {board.approveError ? (
            <span role="alert" className="text-[11.5px] text-[#b91c1c] dark:text-[#fca5a5]">
              {String(translateError(board.approveError, locale).message)}
            </span>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={board.approve}
              disabled={!!board.approving || board.proposal.placements.length === 0}
              aria-busy={!!board.approving}
            >
              {board.approving
                ? fill(t.propApplying, {
                  done: formatCount(board.approving.done, locale),
                  total: formatCount(board.approving.total, locale),
                })
                : t.propApprove}
            </Button>
            <button
              type="button"
              onClick={board.discard}
              disabled={!!board.approving}
              className="text-[11.5px] text-muted-fg underline cursor-pointer"
            >
              {t.propDiscard}
            </button>
          </div>
        </div>
      )}

      {!hasWork ? (
        /* THE WAY OUT, BESIDE THE PROBLEM. An empty board at half past six needs a
           button, not a sentence explaining that work appears when somebody raises it —
           which is what stood here until Sprint 33, while nothing in the product could
           raise one. */
        <EmptyState
          title={t.noWorkTitle}
          body={t.noWorkBody}
          action={<GenerateButton board={board} t={t} />}
        />
      ) : (
        <div className="grid gap-3 [grid-template-columns:minmax(200px,240px)_minmax(0,1fr)] max-lg:[grid-template-columns:minmax(0,1fr)]">
          <Card>
            <CardHeader
              className="px-3 py-2.5"
              title={<span className="text-[13px]">{t.unassigned}</span>}
              subtitle={
                <span className="tnum">
                  {formatCount(view.unassigned.length, locale)} ·{' '}
                  {formatCredits(sum(view.unassigned), locale)} {t.creditsShort}
                </span>
              }
            />
            <CardBody className="flex flex-col gap-1.5 px-2.5 py-2.5">
              {view.unassigned.length === 0
                ? <span className="text-[11.5px] text-muted-fg">{t.laneEmpty}</span>
                : view.unassigned.map((task) => card(task))}
            </CardBody>
          </Card>

          <LaneGrid>
            {view.lanes.map((lane) => (
              <Lane
                key={lane.userId}
                lane={lane}
                locale={locale}
                selected={selected}
                onAssign={move}
                renderCard={(task) => card(task, lane.userId)}
              />
            ))}
          </LaneGrid>
        </div>
      )}
    </div>
  )
}

function Lane({
  lane, locale, selected, onAssign, renderCard,
}: {
  lane: BoardLane
  locale: 'en' | 'ar'
  selected: string | null
  onAssign: (taskId: string, toUserId: string) => void
  renderCard: (task: BoardTask) => React.ReactNode
}) {
  const t = STRINGS[locale]
  const open = canReceiveWork(lane.rosterState)

  // THE SUM OF THE CARDS DRAWN BELOW. Never a number from the server.
  const workload = workloadOf(lane)
  const budget = lane.creditBudget ?? 0

  return (
    <div
      onDragOver={open ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' } : undefined}
      onDrop={open ? (e) => {
        e.preventDefault()
        const taskId = e.dataTransfer.getData('text/plain')
        if (taskId) onAssign(taskId, lane.userId)
      } : undefined}
    >
      <AssignmentLane
        name={laneName(lane, locale)}
        role={
          <>
            {lane.coverage && <>{lane.coverage} · </>}
            {open
              ? (lane.shiftName ? nameIn(locale, lane.shiftName, lane.shiftNameAr) : t.roster.ON_SHIFT)
              : t.roster[lane.rosterState]}
          </>
        }
        initials={initialsOf(laneName(lane, locale))}
        {...(open && lane.creditBudget !== null
          ? {
              load: workload,
              loadMax: budget,
              loadLabel: (
                <>
                  <span>{t.workload}</span>
                  <span className="tnum">
                    {formatCredits(workload, locale)} / {formatCredits(budget, locale)}{' '}
                    {t.creditsShort}
                  </span>
                </>
              ),
            }
          : {})}
        className={cn(!open && 'opacity-70')}
      >
        {lane.tasks.length === 0 && (
          <span className="px-1 text-[11.5px] text-muted-fg">
            {open ? t.laneEmpty : t.notWorking}
          </span>
        )}

        {/* Cards stay draggable and selectable in an inert lane: this is exactly the
            work that needs moving when somebody calls in sick. */}
        {lane.tasks.map(renderCard)}

        {selected && open && (
          <Button
            variant="outline"
            size="sm"
            className="mt-1"
            onClick={() => onAssign(selected, lane.userId)}
          >
            {t.assignHere}
          </Button>
        )}
      </AssignmentLane>
    </div>
  )
}

const sum = (tasks: readonly BoardTask[]) =>
  tasks.reduce((n, task) => n + Math.round(task.credits * 100), 0) / 100

const localisedType = (task: BoardTask, locale: 'en' | 'ar') =>
  nameIn(locale, task.taskTypeName, task.taskTypeNameAr)

/** The attendant's own name, in the reader's script where we hold it. DisplayNameAr is
 *  ours — it is one of the two columns the PMS synchronisation may never write. */
const laneName = (lane: BoardLane, locale: 'en' | 'ar') =>
  nameIn(locale, lane.displayName, lane.displayNameAr)

/** Initials from a display name of unknown script. Falls back to the first character,
 *  which is correct for Arabic where a two-letter initialism is not a convention. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return [...parts[0]!][0] ?? '?'
  return ([...parts[0]!][0] ?? '') + ([...parts[parts.length - 1]!][0] ?? '')
}

/** Named placeholders, for the same reason the error catalogue uses them: Arabic and
 *  English do not order their clauses the same way. */
function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole)
}

/**
 * **THE BUTTON THAT FILLS THE BOARD.**
 *
 * One component, drawn in two places — the toolbar when there is work and the empty state
 * when there is none — because it is the same act and a second copy would be a second set
 * of disabled rules to keep in step.
 *
 * **IT IS NOT DISABLED AFTER A SUCCESSFUL RUN.** Pressing it again is safe and is the
 * intended way to pick up departures the property system reported at nine o'clock. What
 * stops a double-press doing damage is that the sweep is idempotent, not that the control
 * is taken away — *a disabled control must leave somebody something to press.*
 */
function GenerateButton(
  { board, t }: { board: AssignmentBoardState; t: Strings },
) {
  return (
    <Button
      type="button"
      onClick={board.generate}
      disabled={board.generating}
      aria-busy={board.generating}
    >
      <CalendarPlus className="size-3.5" />
      {board.generating ? t.genWorking : t.genButton}
    </Button>
  )
}

/* ============================================================================
   SPRINT 34 — THE SCHEDULE.

   WHY THIS IS ON THE ASSIGNMENT BOARD AND NOT ON A SETTINGS SCREEN. W-129, the property
   configuration screen, is not built. A setting whose only home is an unbuilt screen is
   #90 again — a promise nobody keeps — and the API route would sit there for a sprint or
   six with nothing able to call it. It belongs beside the button it automates anyway:
   somebody wondering whether this could happen by itself is, by definition, standing at
   the button pressing it.
   ========================================================================= */

/** Six o'clock, offered when there is no schedule yet.
 *
 *  NOT MIDNIGHT, which is what `0` would make the first option and what an untouched
 *  select would otherwise save. The morning shift starts at six, the approved pack draws
 *  this board at half past, and a form that quietly defaults to a time nobody wants
 *  punishes the person who opened it to see what the options were. */
/* SEVEN, NOT SIX — AND IT IS AN HOUR BEFORE THE SHIFT, NOT A ROUND NUMBER.
   The client's morning shift starts at eight (16 September). A sweep that runs AT eight
   hands the teams an empty board and a supervisor who is still allocating while they
   stand there. An hour ahead is the whole reason the schedule exists. */
const DEFAULT_SWEEP_MINUTE = 7 * 60

/**
 * The times the form offers: every quarter hour, right round the clock.
 *
 * QUARTER HOURS BECAUSE NOBODY SCHEDULES 06:07. The column takes any minute from 0 to
 * 1439 and the API will accept one — this list is a convenience, not the rule, and a
 * hotel that genuinely wants 06:07 is asking for a longer list rather than a change to
 * the model.
 *
 * AND ROUND THE WHOLE CLOCK, rather than the morning hours a sweep is obviously for.
 * Trimming it to 04:00–11:00 would be this screen deciding a policy the server does not
 * hold, and it would be wrong for the first hotel that wants the day raised the evening
 * before. Ninety-six entries is a long select; it opens scrolled to the current value.
 */
const SWEEP_CHOICES = Array.from({ length: (24 * 60) / 15 }, (_, i) => i * 15)

/**
 * **THE LINE THAT SAYS WHETHER ANYTHING HAPPENS BY ITSELF.**
 *
 * THE TIME IS IN THE LABEL, not behind the control. "Runs automatically at 06:00" answers
 * the only question anybody has about this setting without a press; a line reading
 * "Schedule" with a Change button beside it makes them open a form to find out what the
 * hotel is currently doing, and then decide whether pressing Cancel undid anything.
 */
function ScheduleLine({
  minute, locale, t, onChange,
}: {
  minute: number | null
  locale: 'en' | 'ar'
  t: Strings
  onChange: () => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 text-[12px] text-muted-fg">
      <Clock className="size-3.5 shrink-0" aria-hidden />
      {/* ONE LINE, ALWAYS. Left to wrap, "Runs automatically at 06:00" broke into three
          lines at phone width with the Change control sitting beside the middle one, so the
          row read "automatically Change". The sentence is short enough to hold together at
          any width this screen is used at; it is the ROW that wraps instead. */}
      <span className="tnum whitespace-nowrap">
        {minute === null
          ? t.schedManual
          : fill(t.schedRunsAt, { time: formatClockMinute(minute, locale) })}
      </span>
      <button
        type="button"
        onClick={onChange}
        className="text-[11.5px] text-primary underline cursor-pointer"
      >
        {t.schedChange}
      </button>
    </div>
  )
}

/**
 * **THE FORM.** One choice, and two ways to leave it.
 *
 * A LIST OF TIMES AND NOT A TIME FIELD, and this is the whole reason `formatClockMinute`
 * was written this sprint. `<input type="time">` draws its digits in the BROWSER's locale,
 * not the product's: on an Arabic screen running on an English Windows it shows 06:00 in
 * Western digits, and on some locales a 12-hour clock with an AM/PM control this product
 * does not use anywhere else. Decision D-02 is not a suggestion, and a control whose
 * rendering depends on a setting we cannot see is one we cannot test. Every option here
 * goes through the same function every other clock in the product goes through.
 *
 * It also means there is NOTHING TO PARSE. The value carried is the minute itself, the
 * server's 0–1439 range cannot be breached from this screen, and `work.schedule.
 * minuteOutOfRange` stays what it should be — the API's defence against a caller that is
 * not this form.
 *
 * **AND IT IS THE PLATFORM'S `<select>`, NOT THE LIBRARY'S.** The component library ships
 * a Radix `Select` — a styled button that opens a custom listbox — and this screen would
 * have been its first use. It was written that way first and taken out again, for two
 * reasons that point the same way.
 *
 * The first is that nothing here needs it. A custom listbox earns its keep when the
 * options are rich: an avatar, a second line, a colour. These options are four characters
 * of text that we generate ourselves, which is the case a native control was made for —
 * and on a handset it opens the platform's own wheel, which is better than anything we
 * would build.
 *
 * The second is that the custom listbox, nested inside this dialog, COULD NOT BE
 * EXERCISED. Opening it under the test harness stalls for the better part of a minute,
 * whatever the number of options — a fault of the harness and not of the product, and one
 * no supervisor will ever meet. But the consequence is ours: the only control on this form
 * that carries a value would have shipped with nothing asserting that choosing 05:30 sends
 * 330, on a screen whose entire subject is a number. A control we cannot test is a control
 * whose defects arrive by telephone from Najaf.
 *
 * TURNING IT OFF IS NOT CANCEL, and the two are far apart on purpose. Cancel leaves the
 * hotel as it was; "Turn the schedule off" changes it, and both would otherwise read as
 * "I have finished with this box".
 */
function ScheduleDialog({
  board, t, locale, current, onClose, onSaved,
}: {
  board: AssignmentBoardState
  t: Strings
  locale: 'en' | 'ar'
  current: number | null
  onClose: () => void
  onSaved: (minute: number | null) => void
}) {
  /* SEEDED FROM WHAT THE HOTEL DOES NOW, so Save with nothing touched is a no-op rather
     than a silent move to six o'clock. */
  const [minute, setMinute] = useState<number>(current ?? DEFAULT_SWEEP_MINUTE)

  const busy = board.savingSchedule
  const save = (next: number | null) => {
    void board.setSchedule(next).then((ok) => { if (ok) onSaved(next) })
  }

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogContent>
        <div className="flex items-center gap-2 border-b border-border p-3">
          <Clock className="size-4 text-primary" aria-hidden />
          <DialogTitle className="text-[13.5px] font-semibold">{t.schedTitle}</DialogTitle>
        </div>

        <div className="flex flex-col gap-3 p-3">
          {/* THE INTRO IS THE DIALOG'S DESCRIPTION AND ALSO ITS VISIBLE TEXT — one string,
              read once by everybody. It says what the setting does AND that the button
              stays, because the second is what somebody hesitating over this needs to
              hear: automation that takes the manual path away leaves nothing to press on
              the morning it fails. */}
          <DialogDescription className="text-[12px] leading-relaxed text-muted-fg">
            {t.schedIntro}
          </DialogDescription>

          <Field label={t.schedLabel} hint={t.schedHint}>
            {({ id, 'aria-label': label }) => (
              <select
                id={id}
                aria-label={label}
                value={String(minute)}
                onChange={(e) => setMinute(Number(e.target.value))}
                disabled={busy}
                /* `bg-card` and not transparent: a transparent <select> shows the
                   fieldset through its own dropdown on Windows, which is where this will
                   be used. The box itself is drawn by the Field. */
                className="tnum w-full cursor-pointer border-0 bg-card p-0 text-sm
                           text-foreground outline-none disabled:cursor-not-allowed"
              >
                {SWEEP_CHOICES.map((m) => (
                  <option key={m} value={String(m)}>{formatClockMinute(m, locale)}</option>
                ))}
              </select>
            )}
          </Field>

          {board.scheduleError ? (
            <div
              role="alert"
              className="rounded-hk border border-[#fca5a5] bg-[#fef2f2] px-3 py-2 text-[12px]
                         text-[#b91c1c] dark:border-[#7f1d1d] dark:bg-[#2a0f0f] dark:text-[#fca5a5]"
            >
              {String(translateError(board.scheduleError, locale).message)}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => save(minute)}
              disabled={busy}
              aria-busy={busy}
            >
              {t.save}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onClose} disabled={busy}>
              {t.cancel}
            </Button>

            {/* ONLY WHEN THERE IS ONE. "Turn the schedule off" on a hotel that generates
                by hand is a control that does nothing, and reads as though the schedule
                might be on. */}
            {current !== null && (
              <button
                type="button"
                onClick={() => save(null)}
                disabled={busy}
                className="ms-auto text-[11.5px] text-muted-fg underline cursor-pointer
                           disabled:opacity-50"
              >
                {t.schedOff}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
